/**
 * Kleines ZIP ohne Kompression (Verfahren „stored“) für den Sammel-Download der Rechnungs-PDFs (Befund 23). PDFs sind
 * selbst schon komprimiert; ohne Fremdbibliothek, läuft im Browser und in Node.
 */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/**
 * Dateinamen eindeutig machen („Rechnung.pdf“, „Rechnung (2).pdf“) und Pfadzeichen entfernen. Mit `folders` bleiben
 * Ordner („2026-09/Marke/Datei.pdf“) erhalten; jeder Teil wird für sich bereinigt (Export der Agentur, 0080).
 */
export function uniqueNames(names: readonly string[], opts: { folders?: boolean } = {}): string[] {
  const seen = new Map<string, number>();
  const part = (x: string) => x.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "-").trim().replace(/^\.+/, "");
  return names.map((raw) => {
    const clean = (opts.folders ? raw.split("/").map(part).filter(Boolean).join("/") : raw.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "-").trim()) || "datei";
    const n = (seen.get(clean.toLowerCase()) ?? 0) + 1;
    seen.set(clean.toLowerCase(), n);
    if (n === 1) return clean;
    const dot = clean.lastIndexOf(".");
    return dot > 0 ? `${clean.slice(0, dot)} (${n})${clean.slice(dot)}` : `${clean} (${n})`;
  });
}

export function zipFiles(files: readonly { name: string; data: Uint8Array; date?: Date }[], opts: { folders?: boolean } = {}): Uint8Array {
  const enc = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  const names = uniqueNames(files.map((f) => f.name), opts);
  files.forEach((f, idx) => {
    const name = enc.encode(names[idx]!);
    const crc = crc32(f.data);
    const d = f.date ?? new Date();
    const time = (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2);
    const date = ((Math.max(1980, d.getFullYear()) - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(6, 0x0800, true); // Dateinamen in UTF-8
    lv.setUint16(8, 0, true);
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, f.data.length, true);
    lv.setUint32(22, f.data.length, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true);
    local.set(name, 30);
    const central = new Uint8Array(46 + name.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, f.data.length, true);
    cv.setUint32(24, f.data.length, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    central.set(name, 46);
    locals.push(local, f.data);
    centrals.push(central);
    offset += local.length + f.data.length;
  });
  const centralSize = centrals.reduce((a, c) => a + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);
  const out = new Uint8Array(offset + centralSize + end.length);
  let p = 0;
  for (const part of [...locals, ...centrals, end]) {
    out.set(part, p);
    p += part.length;
  }
  return out;
}
